import { State } from "xstate";
import {
  getChildState,
  getNestedChildState,
  ParentState,
} from "@capone/checkout";
import { PersonId, Person } from "redmond";
import { getDateTimeWithFormat } from "halifax";
import dayjs from "dayjs";

import { ActivityContext } from "../../../modules/book/state/types";
import {
  TravelerInformationState,
  TravelerInformationChildState,
} from "./types";

import {
  getRequiredBookingQuestions,
  checkTravelerBookingQuestions,
} from "./validation";

type TravelerStateType = State<ActivityContext>;
type TravelerStateWithoutValue = Pick<TravelerStateType, "context">;

export const getIsTravelerInformationLoading = (state: TravelerStateType) => {
  const childState = getChildState(state.value) as TravelerInformationState;
  const nestedChildState = getNestedChildState(
    state.value
  ) as TravelerInformationChildState;

  return (
    childState === TravelerInformationState.loading ||
    [
      TravelerInformationChildState.add,
      TravelerInformationChildState.delete,
      TravelerInformationChildState.update,
    ].includes(nestedChildState)
  );
};

export const getUserTravelers = ({ context }: TravelerStateWithoutValue) =>
  context[ParentState.activityTravelerInformation].userTravelers;

export const getSelectedTravelerIds = ({
  context,
}: TravelerStateWithoutValue) =>
  context[ParentState.activityTravelerInformation].selectedTravelerIds;

export const getAllSelectedUserTravelers = (
  state: TravelerStateWithoutValue
) => {
  const selectedTravelerIds = getSelectedTravelerIds(state);
  const userTravelers = getUserTravelers(state);

  return userTravelers.filter((traveler) =>
    selectedTravelerIds.includes(traveler.id)
  );
};

export const getCurrentUserTraveler = ({
  context,
}: TravelerStateWithoutValue) =>
  context[ParentState.activityTravelerInformation].currentUserTraveler;

export const getOpenTravelerFormModal = ({ value }: TravelerStateType) =>
  TravelerInformationState.travelerForm ===
  (getChildState(value) as TravelerInformationState);

export const getTravelerErrorOpen = ({ value }: TravelerStateType) => {
  const stateValue = getChildState(value);
  return TravelerInformationState.error === stateValue;
};

export const getTravelerError = ({ context }: TravelerStateWithoutValue) =>
  context[ParentState.activityTravelerInformation].error;

export const getTravelerVisited = ({ context }: TravelerStateWithoutValue) =>
  context[ParentState.activityTravelerInformation].visited;

export const getTravelerStateValidated = (state: TravelerStateWithoutValue) =>
  getSelectedTravelerIds(state).length > 0;

export const getSelectedPrimaryTravelerId = (
  state: TravelerStateWithoutValue
) =>
  state.context[ParentState.activityPrimaryTraveler].selectedPrimaryTravelerId;

export const getSelectedPrimaryTraveler = (
  state: TravelerStateWithoutValue
): Person | undefined => {
  const selectedPrimaryTravelerId = getSelectedPrimaryTravelerId(state);
  const { userTravelers } =
    state.context[ParentState.activityTravelerInformation];

  return userTravelers?.find(
    (person) => person.id === selectedPrimaryTravelerId
  );
};

export const getPerTravelerBookingQuestion = ({
  context,
}: TravelerStateWithoutValue) => {
  const { requirePassport, requireHeight, requireWeight } =
    getRequiredBookingQuestions(context);
  const { bookingQuestionResponse } = context.activityTravelerInformation;

  return {
    requirePassport,
    requireHeight,
    requireWeight,
    hasAnsweredBookingQuestions: (travelerId: PersonId) =>
      checkTravelerBookingQuestions(travelerId, context),
    getCurrentTravelerBookingQuestionResponse: (travelerId: PersonId) => {
      if (!bookingQuestionResponse || !bookingQuestionResponse[travelerId]) {
        return {
          weight: "",
          heightFt: "",
          heightIn: "",
        };
      }

      return bookingQuestionResponse[travelerId];
    },
  };
};
interface AgeBandRequirement {
  numberOfTravelers: number;
  lowerBound: number;
  upperBound: number;
}

export const getDistinctAgeBands = (state: TravelerStateWithoutValue) => {
  const ageBands =
    state.context.activityShop?.bookableItems
      .flatMap((item) => Object.values(item.ageBandPricesByDate).flat())
      .map((ageBandPrice) => ({
        ageBand: ageBandPrice.ageBand,
        lowerBound: ageBandPrice.lowerBound,
        upperBound: ageBandPrice.upperBound,
      })) ?? [];

  // one item per age band type. The min / max ages should be constant between them, only the prices min / max travelers change
  return Array.from(
    new Map(ageBands.map((item) => [item.ageBand, item])).values()
  );
};

export const getAreSelectedTravelersValidForAgeBands = (
  state: TravelerStateWithoutValue
) => {
  const selectedTravelers = getAllSelectedUserTravelers(state);
  if (selectedTravelers.length === 0) {
    return true;
  }

  const { priceBreakdown } = state.context;
  if (!priceBreakdown) {
    return false;
  }

  const distinctAgeBands = getDistinctAgeBands(state);

  const requiredAgeBands: Record<string, AgeBandRequirement> = {};
  priceBreakdown.lineItems.forEach((lineItem) => {
    const { ageBand, numberOfTravelers } = lineItem.specificAgeBand;

    // find the age band in the distinctAgeBands array
    // can be replaced if lineItem.specificAgeBand returns the correct lowerBound and upperBound
    const { lowerBound = -Infinity, upperBound = Infinity } =
      distinctAgeBands.find((ageBandItem) => ageBandItem.ageBand === ageBand) ||
      {};

    requiredAgeBands[ageBand] = {
      numberOfTravelers,
      lowerBound,
      upperBound,
    };
  });

  // check if the selected travelers meet the age band bounds
  // and reduce the number of travelers required for each age band
  selectedTravelers.forEach((traveler) => {
    const age = dayjs().diff(
      getDateTimeWithFormat(traveler.dateOfBirth, "YYYY-MM-DD"),
      "year"
    );

    Object.keys(requiredAgeBands).forEach((ageBand) => {
      const { lowerBound, upperBound } = requiredAgeBands[ageBand];

      if (age >= lowerBound && age <= upperBound) {
        requiredAgeBands[ageBand].numberOfTravelers -= 1;

        if (requiredAgeBands[ageBand].numberOfTravelers <= 0) {
          delete requiredAgeBands[ageBand];
        }
      }
    });
  });

  return Object.keys(requiredAgeBands).length === 0;
};

export const getTravelerTypeByAge = (state: TravelerStateWithoutValue) => {
  const distinctAgeBands = getDistinctAgeBands(state);

  return (age: number) => {
    // find the age band in the distinctAgeBands array
    const ageBand = distinctAgeBands.find(
      (band) => age >= band.lowerBound && age <= band.upperBound
    );

    // if no age band is found, return undefined
    return ageBand
      ? ageBand.ageBand
      : "Traveler's age is not eligible for this activity";
  };
};
